'use client';
import type { ComponentType } from 'react';

import { UserAvatarDisplay } from '@/components/user-avatar-display';
import {
  CalendarClock,
  CircleUserRound,
  FolderKanban,
  MessageSquare,
  Package,
  SwatchBook,
} from 'lucide-react';
import { ItemIcon, MenuGroup, MenuItem, ShortcutHint } from './primitives';
import type {
  AestheticResult,
  AvatarResult,
  CommandDef,
  MemberResult,
  OrgThreadResult,
  ProductResult,
  LinkResult,
  ProjectResult,
  ScheduleResult,
} from './types';

/** 28px square thumbnail tile matching ItemIcon geometry. */
function ThumbTile({
  src,
  alt,
  fallbackIcon,
}: {
  src?: string;
  alt: string;
  fallbackIcon: React.ComponentType<{ className?: string }>;
}) {
  if (!src) return <ItemIcon icon={fallbackIcon} />;
  return (
    <span className="bg-muted ring-border/40 flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-lg ring-1 ring-inset">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={src} alt={alt} className="size-full object-cover" />
    </span>
  );
}

export function ProductRows({
  products,
  heading,
  formatTime,
  onSelect,
}: {
  products: ProductResult[];
  heading: string;
  formatTime: (timestamp: number) => string;
  onSelect: (product: ProductResult) => void;
}) {
  if (products.length === 0) return null;
  return (
    <MenuGroup heading={heading}>
      {products.map((product) => {
        const updatedAt =
          product.updatedAt ?? product.createdAt ?? product._creationTime;
        return (
          <MenuItem
            key={String(product._id)}
            value={`product:${String(product._id)}`}
            onSelect={() => onSelect(product)}
          >
            <ItemIcon icon={Package} />
            <span className="min-w-0 flex-1 truncate">{product.name}</span>
            <span className="text-muted-foreground shrink-0 text-xs">
              {product.category && <span>{product.category}</span>}
              {product.category && updatedAt && <span> · </span>}
              {updatedAt && <span>{formatTime(updatedAt)}</span>}
            </span>
          </MenuItem>
        );
      })}
    </MenuGroup>
  );
}

export function ThreadRows({
  threads,
  heading,
  formatTime,
  onSelect,
}: {
  threads: OrgThreadResult[];
  heading: string;
  formatTime: (timestamp: number) => string;
  onSelect: (thread: OrgThreadResult) => void;
}) {
  if (threads.length === 0) return null;
  return (
    <MenuGroup heading={heading}>
      {threads.map((thread) => {
        const updatedAt = thread.updatedAt ?? thread._creationTime;
        return (
          <MenuItem
            key={String(thread._id)}
            value={`thread:${String(thread._id)}`}
            onSelect={() => onSelect(thread)}
          >
            <ItemIcon icon={MessageSquare} />
            <span className="min-w-0 flex-1 truncate">
              {thread.title || thread.threadId}
            </span>
            {updatedAt && (
              <span className="text-muted-foreground shrink-0 text-xs">
                {formatTime(updatedAt)}
              </span>
            )}
          </MenuItem>
        );
      })}
    </MenuGroup>
  );
}

export function ProjectRows({
  projects,
  heading,
  formatTime,
  onSelect,
}: {
  projects: ProjectResult[];
  heading: string;
  formatTime: (timestamp: number) => string;
  onSelect: (project: ProjectResult) => void;
}) {
  if (projects.length === 0) return null;
  return (
    <MenuGroup heading={heading}>
      {projects.map((project) => (
        <MenuItem
          key={String(project._id)}
          value={`project:${String(project._id)}`}
          onSelect={() => onSelect(project)}
        >
          <ItemIcon icon={FolderKanban} />
          <span className="min-w-0 flex-1 truncate">{project.name}</span>
          <span className="text-muted-foreground shrink-0 text-xs">
            {formatTime(project.updatedAt)}
          </span>
        </MenuItem>
      ))}
    </MenuGroup>
  );
}

export function LinkRows({
  links,
  heading,
  icon,
  formatTime,
  onSelect,
}: {
  links: LinkResult[];
  heading: string;
  icon: ComponentType<{ className?: string }>;
  formatTime: (timestamp: number) => string;
  onSelect: (link: LinkResult) => void;
}) {
  if (links.length === 0) return null;
  return (
    <MenuGroup heading={heading}>
      {links.map((link) => (
        <MenuItem
          key={link.id}
          value={`link:${link.id}`}
          onSelect={() => onSelect(link)}
        >
          <ItemIcon icon={icon} />
          <span className="min-w-0 flex-1 truncate">{link.title}</span>
          {link.hint || link.at !== undefined ? (
            <span className="text-muted-foreground shrink-0 text-xs">
              {link.hint ?? formatTime(link.at as number)}
            </span>
          ) : null}
        </MenuItem>
      ))}
    </MenuGroup>
  );
}

export function ScheduleRows({
  schedules,
  heading,
  onSelect,
}: {
  schedules: ScheduleResult[];
  heading: string;
  onSelect: (schedule: ScheduleResult) => void;
}) {
  if (schedules.length === 0) return null;
  return (
    <MenuGroup heading={heading}>
      {schedules.map((schedule) => (
        <MenuItem
          key={String(schedule._id)}
          value={`schedule:${String(schedule._id)}`}
          onSelect={() => onSelect(schedule)}
        >
          <ItemIcon icon={CalendarClock} />
          <span className="min-w-0 flex-1 truncate">{schedule.title}</span>
          <span className="text-muted-foreground shrink-0 text-xs">
            {schedule.hint}
          </span>
        </MenuItem>
      ))}
    </MenuGroup>
  );
}

export function AvatarRows({
  avatars,
  heading,
  onSelect,
}: {
  avatars: AvatarResult[];
  heading: string;
  onSelect: (avatar: AvatarResult) => void;
}) {
  if (avatars.length === 0) return null;
  return (
    <MenuGroup heading={heading}>
      {avatars.map((avatar) => (
        <MenuItem
          key={String(avatar._id)}
          value={`avatar:${String(avatar._id)}`}
          onSelect={() => onSelect(avatar)}
        >
          <ThumbTile
            src={avatar.thumbnailUrl}
            alt={avatar.name}
            fallbackIcon={CircleUserRound}
          />
          <span className="min-w-0 flex-1 truncate">{avatar.name}</span>
        </MenuItem>
      ))}
    </MenuGroup>
  );
}

export function AestheticRows({
  aesthetics,
  heading,
  onSelect,
}: {
  aesthetics: AestheticResult[];
  heading: string;
  onSelect: (aesthetic: AestheticResult) => void;
}) {
  if (aesthetics.length === 0) return null;
  return (
    <MenuGroup heading={heading}>
      {aesthetics.map((aesthetic) => (
        <MenuItem
          key={String(aesthetic._id)}
          value={`aesthetic:${String(aesthetic._id)}`}
          onSelect={() => onSelect(aesthetic)}
        >
          <ThumbTile
            src={aesthetic.coverImageUrl}
            alt={aesthetic.name}
            fallbackIcon={SwatchBook}
          />
          <span className="min-w-0 flex-1 truncate">{aesthetic.name}</span>
          {aesthetic.usecase && (
            <span className="text-muted-foreground shrink-0 text-xs capitalize">
              {aesthetic.usecase}
            </span>
          )}
        </MenuItem>
      ))}
    </MenuGroup>
  );
}

export function MemberRows({
  members,
  heading,
  roleLabels,
  onSelect,
}: {
  members: MemberResult[];
  heading: string;
  roleLabels: Record<'owner' | 'admin' | 'member', string>;
  onSelect: (member: MemberResult) => void;
}) {
  if (members.length === 0) return null;
  return (
    <MenuGroup heading={heading}>
      {members.map((member) => (
        <MenuItem
          key={String(member.userId)}
          value={`member:${String(member.userId)}`}
          onSelect={() => onSelect(member)}
        >
          <UserAvatarDisplay
            user={{
              name: member.name,
              email: member.email,
              image: member.image,
            }}
            size={28}
            className="shrink-0"
          />
          <span className="min-w-0 flex-1 truncate">
            {member.name || member.email}
          </span>
          <span className="text-muted-foreground shrink-0 text-xs">
            {roleLabels[member.role]}
          </span>
        </MenuItem>
      ))}
    </MenuGroup>
  );
}

export function CommandRows({
  commands,
  heading,
  onRun,
}: {
  commands: CommandDef[];
  heading: string;
  onRun: (command: CommandDef) => void;
}) {
  const uniqueCommands = commands.filter(
    (command, index, allCommands) =>
      allCommands.findIndex((candidate) => candidate.id === command.id) ===
      index
  );
  if (uniqueCommands.length === 0) return null;
  return (
    <MenuGroup heading={heading}>
      {uniqueCommands.map((command) => {
        const Icon = command.icon;
        return (
          <MenuItem
            key={command.id}
            value={`command:${command.id}`}
            onSelect={() => onRun(command)}
            className={
              command.destructive
                ? 'text-destructive data-[selected=true]:text-destructive'
                : undefined
            }
          >
            {Icon && (
              <ItemIcon
                icon={Icon}
                className={
                  command.destructive
                    ? 'ring-destructive/20 bg-destructive/5'
                    : undefined
                }
                iconClassName={
                  command.destructive ? 'text-destructive' : undefined
                }
              />
            )}
            <span className="min-w-0 flex-1 truncate">{command.label}</span>
            {command.badge !== undefined && (
              <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                {command.badge.toLocaleString()}
              </span>
            )}
            {command.shortcutId && (
              <ShortcutHint shortcutId={command.shortcutId} />
            )}
          </MenuItem>
        );
      })}
    </MenuGroup>
  );
}
